/**
 * Fundraising campaigns.
 *
 * A committee raises money for one thing that is not rent: a generator, a water
 * tank, repainting the stairwell. Residents give what they can, when they can,
 * and a campaign either reaches its figure or it does not.
 *
 * Two rules shape everything here. A contribution is voluntary, so it is only
 * ever a credit and never a charge: nobody is put into arrears by a campaign,
 * and a unit that gives nothing owes nothing. And a contribution lives in its
 * own ledger bucket, so it cannot settle a month and cannot move a rent balance
 * by a single birr, which is what stops a generator fund quietly paying
 * somebody's rent.
 *
 * The figure on a campaign document is a projection, like every other total in
 * this codebase. It is rebuilt from the ledger by a function, and the arithmetic
 * that rebuilds it is here so the committee's screen and the server agree.
 */

import { type LedgerEntry, isRentEntry } from "./ledger.js";
import { type Birr, assertBirr } from "./money.js";

export class CampaignError extends Error {}

export const CAMPAIGN_STATUSES = ["open", "closed"] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export interface CampaignDoc {
  orgId: string;
  buildingId: string;
  /** What the money is for, in the words a resident will read. */
  title: string;
  /** A sentence of detail, or nothing. */
  purpose: string | null;
  target: Birr;
  status: CampaignStatus;
  /** The last day money is taken, or null for a campaign with no end. ISO 8601. */
  closesAt: string | null;
  /**
   * Rebuilt from the ledger by a function, never written by a client. Present
   * so a resident can see how far along a campaign is without reading six
   * hundred other units' entries, which the rules would refuse anyway.
   */
  raised: Birr;
  /** How many units have given something that has not been reversed. */
  givers: number;
}

export interface CampaignProgress {
  raised: Birr;
  target: Birr;
  /** What is still needed. Never below zero: a campaign can be overshot. */
  remaining: Birr;
  /** Raised over target, 0 upwards. A campaign asking for nothing is met. */
  share: number;
  met: boolean;
  givers: number;
}

export function campaignProgress(campaign: {
  raised: Birr;
  target: Birr;
  givers: number;
}): CampaignProgress {
  const { raised, target, givers } = campaign;
  return {
    raised,
    target,
    remaining: Math.max(target - raised, 0),
    share: target <= 0 ? 1 : raised / target,
    met: raised >= target,
    givers,
  };
}

/**
 * Whether this campaign still takes money.
 *
 * A closed campaign is closed whatever the date says, because a committee may
 * stop one early. A close date is the last day money is taken, so a campaign
 * closing on the 31st is still open all through the 31st.
 */
export function campaignIsOpen(
  campaign: { status: CampaignStatus; closesAt: string | null },
  now: Date,
): boolean {
  if (campaign.status !== "open") {
    return false;
  }
  if (campaign.closesAt === null) {
    return true;
  }
  const closes = Date.parse(campaign.closesAt);
  return Number.isNaN(closes) ? false : now.getTime() <= closes;
}

/**
 * What a campaign has raised, from the entries themselves.
 *
 * Contributions are credits, so they are negative in the ledger and this
 * reports the figure a committee would say out loud. A reversal is a positive
 * entry against the same campaign, so reversing a contribution takes the money
 * back out of the total and, if it was all that unit gave, takes them out of
 * the count of givers too.
 */
export function campaignTotals(
  entries: readonly LedgerEntry[],
  campaignId: string,
): { raised: Birr; givers: number } {
  const byUnit = new Map<string, Birr>();

  for (const entry of entries) {
    if (isRentEntry(entry) || entry.campaignId !== campaignId) {
      continue;
    }
    byUnit.set(entry.unitId, (byUnit.get(entry.unitId) ?? 0) - entry.amount);
  }

  let raised = 0;
  let givers = 0;
  for (const given of byUnit.values()) {
    raised += given;
    if (given > 0) {
      givers += 1;
    }
  }

  return { raised, givers };
}

/**
 * One contribution per payment, and the id says which. Derived rather than
 * random for the same reason every other ledger id is: verifying the same
 * payment twice writes the same document instead of the money twice.
 */
export const contributionEntryId = (paymentId: string): string => `con_${paymentId}`;

/** The reversal of a contribution, which is the only way one is undone. */
export const contributionReversalId = (paymentId: string): string => `con_${paymentId}_rev`;

export interface CampaignDraft {
  title: string;
  purpose: string | null;
  target: Birr;
  closesAt: string | null;
}

/**
 * Checking a campaign before it is created, in the one place both the screen
 * that offers the form and the function that writes the document can use.
 *
 * A campaign with no target is a collection tin: the screen cannot draw
 * progress, the committee cannot say whether it worked, and there is nothing to
 * close it. So a target is required and has to be real money.
 */
export function assertCampaignDraft(draft: CampaignDraft): void {
  if (draft.title.trim() === "") {
    throw new CampaignError("A campaign needs a title saying what the money is for.");
  }
  assertBirr(draft.target, "target");
  if (draft.target <= 0) {
    throw new CampaignError(
      `A campaign needs a target above zero, received ${String(draft.target)}`,
    );
  }
  if (draft.closesAt !== null && Number.isNaN(Date.parse(draft.closesAt))) {
    throw new CampaignError(`"${draft.closesAt}" is not a date this campaign can close on.`);
  }
}
